import React, { useState, useEffect, useCallback } from 'react';
import { sliderData } from '../data/mock';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % sliderData.length);
  }, []);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderData.length) % sliderData.length);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <section id="inicio" className="relative w-full h-[85vh] min-h-[600px] overflow-hidden">
      {/* Slides */}
      {sliderData.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
            index === currentSlide 
              ? 'opacity-100 scale-100' 
              : 'opacity-0 scale-105'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover"
            style={{ objectPosition: slide.objectPosition || 'center' }}
          />
        </div>
      ))}

      {/* Overlays for depth and professionalism */}
      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30 z-10" />
      
      {/* Industrial texture pattern */}
      <div 
        className="absolute inset-0 z-10 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Diagonal lines accent */}
      <div 
        className="absolute inset-0 z-10 opacity-[0.02]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,0.1) 2px,
            rgba(255,255,255,0.1) 4px
          )`,
        }}
      />

      {/* Accent color bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 md:w-2 bg-gradient-to-b from-[#f4a261] via-[#0d3b66] to-transparent z-20" />

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="container mx-auto px-8 lg:px-16 max-w-7xl">
          <div className="max-w-3xl">
            {/* Decorative line */}
            <div className="flex items-center gap-4 mb-6">
              <div className="h-[2px] w-12 bg-[#f4a261]" />
              <span className="text-[#f4a261] text-sm font-semibold tracking-[0.2em] uppercase">
                Mecanizados de Precisión
              </span>
            </div>

            {/* Main title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.1] mb-6 tracking-tight">
              Aseguramos el
              <span className="block text-[#f4a261]">mejor servicio</span>
            </h1>

            {/* Subtitle */}
            <p className="text-white/80 text-lg md:text-xl lg:text-2xl font-light leading-relaxed max-w-2xl mb-10">
              Ofrecemos las soluciones industriales más completas en materia de fijación, control y mecanizado de precisión.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-[#f4a261] text-[#1a1c1b] font-bold uppercase tracking-wider rounded hover:bg-[#e8965a] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-[#f4a261]/30"
              >
                Contactar
              </button>
              <button 
                onClick={() => document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 border-2 border-white/30 text-white font-bold uppercase tracking-wider rounded hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                Ver Servicios
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/10 hover:bg-[#f4a261] transition-all duration-300 text-white hover:text-[#1a1c1b] backdrop-blur-sm border border-white/20 hover:border-[#f4a261] group"
        aria-label="Anterior"
      >
        <ChevronLeft size={28} className="group-hover:scale-110 transition-transform" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/10 hover:bg-[#f4a261] transition-all duration-300 text-white hover:text-[#1a1c1b] backdrop-blur-sm border border-white/20 hover:border-[#f4a261] group"
        aria-label="Siguiente"
      >
        <ChevronRight size={28} className="group-hover:scale-110 transition-transform" />
      </button>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3">
        {sliderData.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-500 rounded-full ${
              index === currentSlide
                ? 'w-10 h-3 bg-[#f4a261]'
                : 'w-3 h-3 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Ir a slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0d3b66] via-[#f4a261] to-[#0d3b66] z-20" />

      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 z-20 overflow-hidden pointer-events-none">
        <div className="absolute top-4 right-4 w-20 h-20 border-t-2 border-r-2 border-[#f4a261]/30" />
      </div>
      <div className="absolute bottom-0 left-0 w-32 h-32 z-20 overflow-hidden pointer-events-none">
        <div className="absolute bottom-4 left-4 w-20 h-20 border-b-2 border-l-2 border-[#f4a261]/30" />
      </div>
    </section>
  );
};

export default HeroSlider;
